import "./polyfills";

import * as Sentry from "@sentry/vue";
import { useEventListener } from "@vueuse/core";
import { cloneDeep } from "lodash-es";
import { createPinia } from "pinia";
import { createPersistedState } from "pinia-plugin-persistedstate";
import { createSharedElementDirective } from "v-shared-element";
import { createApp } from "vue";
import VueMatomo from "vue-matomo";
import Toast, { type PluginOptions as ToastPluginOptions } from "vue-toastification";

import App from "@/App.vue";
import { config } from "@/config";
import { i18n } from "@/i18n";
import { router } from "@/router";
import { Cookies } from "@/services";

import { MatomoHelper } from "./helpers";

const pinia = createPinia()
  .use(
    createPersistedState({
      storage: sessionStorage
    })
  )
  // Make sure that all stores have the $reset() function.
  // Can be removed when Pinia stops requiring it for non-Options stores.
  .use(({ store }) => {
    const initialState = cloneDeep(store.$state);
    store.$reset = () => store.$patch(cloneDeep(initialState));
  });

const app = createApp(App);

if (config.app.isProduction) {
  Sentry.init({
    app,
    tracesSampleRate: 0.05,
    attachProps: true,
    attachStacktrace: true,
    trackComponents: true,
    logErrors: true,
    dsn: config.app.sentry.dsn
  });
}

const { matches: prefersReducedMotion } = window.matchMedia("(prefers-reduced-motion: reduce)");
const sharedElementDirective = createSharedElementDirective({
  easing: "ease-in",
  duration: prefersReducedMotion ? "0" : "100ms"
});

app.use(router);
app.use(i18n);
app.use(pinia);
app.use(sharedElementDirective);

app.use(Toast, <ToastPluginOptions>{
  toastClassName: "le-alert__toast",
  containerClassName: "le-alert",
  draggable: true,
  draggablePercent: 0.6,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  closeOnClick: false,
  closeButton: false,
  icon: false,
  maxToasts: 1,
  position: "bottom-center",
  shareAppContext: true
});

app.use(VueMatomo, {
  host: config.app.matomo.host,
  siteId: config.app.matomo.siteId,
  router,
  disableCookies: true,
  userId: Cookies.get("pcident"),
  trackInitialView: false // vue-router will always trigger afterEach on initial page view.
});

// Stateless location changes from Angular is not caught by Vue router,
// so we have to trigger a page view manually
useEventListener("ng:location-stateless-change", (event) => {
  MatomoHelper.trackPageView(event.detail.url);
});

app.mount("#lemue-app");
